/**
 * @module app/router
 * @description Minimal hash router.
 *
 * Hash routing chosen deliberately: it needs no server rewrite rules, so the
 * identical build runs on Cloudflare Pages, in a sub-folder of theafj.org,
 * or embedded in a WordPress/Elementor page without IT touching Apache/Nginx.
 */
import { useSyncExternalStore } from "react";
import { CAMPAIGN } from "../config/campaign";

export type Route =
    | { name: "home" }
    | { name: "card"; slug: string }
    | { name: "pledge"; fromSlug: string | null }
    | { name: "screen" }
    | { name: "admin" }
    | { name: "terms" }
    | { name: "print" }
    | { name: "thank-you"; slug: string | null };

/** Parses a location hash such as "#/card/mission-of-sight" into a Route. */
export function parseHash(hash: string): Route {
    const [path, query = ""] = hash.replace(/^#/, "").split("?");
    const parts = path.split("/").filter(Boolean);
    const params = new URLSearchParams(query);
    switch (parts[0]) {
        case "card":
            return parts[1] ? { name: "card", slug: parts[1] } : { name: "home" };
        case "pledge":
            return { name: "pledge", fromSlug: params.get("from") };
        case "screen":
            return { name: "screen" };
        case "admin":
            return { name: "admin" };
        case "terms":
            return { name: "terms" };
        case "print":
            return { name: "print" };
        case "thank-you":
            return { name: "thank-you", slug: parts[1] ?? null };
        default:
            return { name: "home" };
    }
}

/** Builds the hash for a route; the inverse of parseHash. */
export function hrefFor(route: Route): string {
    switch (route.name) {
        case "card":
            return `#/card/${route.slug}`;
        case "pledge":
            return route.fromSlug ? `#/pledge?from=${encodeURIComponent(route.fromSlug)}` : "#/pledge";
        case "screen":
            return "#/screen";
        case "admin":
            return "#/admin";
        case "terms":
            return "#/terms";
        case "print":
            return "#/print";
        case "thank-you":
            return route.slug ? `#/thank-you/${route.slug}` : "#/thank-you";
        default:
            return "#/";
    }
}

/**
 * Absolute shareable URL for a route (share links, QR codes).
 * Uses CAMPAIGN.publicAppUrl when set, so a print kit generated on a preview
 * deploy still points at the production site. The `?edition=` review
 * parameter is deliberately dropped: shared links follow the live edition.
 */
export function absoluteUrlFor(route: Route): string {
    return `${appBaseUrl()}${hrefFor(route)}`;
}

/** The app's public base address, ending in "/". */
function appBaseUrl(): string {
    return CAMPAIGN.publicAppUrl ?? `${window.location.origin}${window.location.pathname.replace(/[^/]*$/, "")}`;
}

/**
 * Link to share for a story card. Deploy builds emit a static preview page
 * per card at card/<slug>/ (build/sharePages.ts) so Facebook, LinkedIn,
 * WhatsApp and iMessage show that card's photo and story; it forwards
 * people to #/card/<slug>. Builds without a public address have no such
 * pages, so they share the in-app link.
 */
export function shareUrlForCard(slug: string): string {
    return import.meta.env.PROD && CAMPAIGN.publicAppUrl ? `${CAMPAIGN.publicAppUrl}card/${slug}/` : absoluteUrlFor({ name: "card", slug });
}

const subscribe = (listener: () => void) => {
    window.addEventListener("hashchange", listener);
    return () => window.removeEventListener("hashchange", listener);
};

/** Current route; re-renders on navigation. */
export function useRoute(): Route {
    const hash = useSyncExternalStore(subscribe, () => window.location.hash);
    return parseHash(hash);
}
